import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { outreachBuilderPath } from '../outreachPaths';
import type { CampaignResultsPage } from '../outreachTypes';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the results; each part uses its own slice of it */
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WarmPeopleProps {
  results: CampaignResultsPage;
  createFromWarm: (catalogItemUuid: string) => Promise<{ campaignUuid: string; warm: number }>;
  isCreating: boolean;
}

/**
 * "Warm people for your next song" (design outreach-v2 n1): the people who listened, replied or
 * added. "Pitch <next song> to them first" makes a draft campaign with them first in line
 * (`createFromWarm`) and opens its setup. No warm person: no card.
 */
const WarmPeopleComponent: React.FC<WarmPeopleProps> = ({ results, createFromWarm, isCreating }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { isDemo } = useOutreachPlan();
  const { showError } = useShowToast();
  const next = results.nextSong;
  const start = useCallback(() => {
    if (next === null) {
      return;
    }
    createFromWarm(next.catalogItemUuid)
      .then(({ campaignUuid }) => {
        // eslint-disable-next-line no-void
        void navigate(outreachBuilderPath({ campaign: campaignUuid, song: next.catalogItemUuid }));
      })
      .catch((error: unknown) => {
        console.error(error);
        showError(intl.formatMessage(messages.warmFailed));
      });
  }, [createFromWarm, intl, navigate, next, showError]);

  if (results.warm === 0) {
    return null;
  }
  return (
    <>
      <div className={styles.warm} data-testid="warm-people">
        <div className={styles.warmNumber}>{intl.formatNumber(results.warm)}</div>
        <div className="flex-grow-1">
          <b>{intl.formatMessage(messages.warmTitle)}</b>
          <span>
            {next === null
              ? intl.formatMessage(messages.warmBodyNoNext, { song: results.songTitle })
              : intl.formatMessage(messages.warmBody, { song: results.songTitle, next: next.title })}
          </span>
        </div>
      </div>
      {next !== null && (
        <SMLButton
          color="primary"
          omitCap
          onClick={start}
          isLoading={isCreating}
          disabled={isDemo || isCreating}
          testID="warm-pitch-next"
        >
          {intl.formatMessage(messages.warmButton, { next: next.title })}
        </SMLButton>
      )}
    </>
  );
};

export const WarmPeople = TypedMemo(WarmPeopleComponent);
